import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import { formatMoneyFromMinorUnits } from '../../../shared/lib/money.js';
import { getEnumOptionLabel } from '../../../shared/i18n/enum-labels.js';
import { readVerificationAccess } from '../model/verification-access.js';

export default function VerificationAccessCard({ profileId, refreshKey }) {
  const { token, hasPermission } = useAuth();
  const { t, i18n } = useTranslation();
  const [state, setState] = useState({ access: null, error: '', loading: true });
  const [reload, setReload] = useState(0);
  const canView = hasPermission('kyc.view');
  const locale = i18n?.resolvedLanguage || i18n?.language;

  useEffect(() => {
    let cancelled = false;
    setState({ access: null, error: '', loading: true });
    if (canView && profileId) {
      adminApi.getProfileVerificationAccess(token, profileId).then((response) => {
        if (!cancelled) setState({ access: readVerificationAccess(response), error: '', loading: false });
      }).catch((failure) => {
        if (!cancelled) setState({ access: null, error: failure.message || 'verificationAccess.loadError', loading: false });
      });
    }
    return () => { cancelled = true; };
  }, [token, profileId, canView, refreshKey, reload]);

  if (!canView || !profileId) return null;
  const { access, error, loading } = state;
  const date = (value) => value ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)) : t('verificationAccess.notStarted');
  const reasonKey = `verificationAccess.reasons.${access?.depositBlockReason}`;
  return <section className="card card-body mb-3" aria-labelledby="verification-access-title">
    <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
      <h3 className="h5 mb-0" id="verification-access-title">{t('verificationAccess.title')}</h3>
      <button type="button" className="btn btn-sm btn-default" disabled={loading} onClick={() => setReload((value) => value + 1)}>{t('verificationAccess.refresh')}</button>
    </div>
    {loading && <p role="status" className="mb-0">{t('verificationAccess.loading')}</p>}
    {error && <p role="alert" className="text-danger mb-0">{t(error)}</p>}
    {access && <>
      <p className="mb-2"><span className={`badge ${access.verified ? 'bg-success' : 'bg-warning text-dark'}`}>{access.verified ? t('verificationAccess.verified') : t('verificationAccess.unverified')}</span> <span className="text-muted">{getEnumOptionLabel(t, 'profileStatus', access.profileStatus, '—')}</span></p>
      <dl className="row mb-0">
        {['allowLiveAccount', 'allowDeposit', 'allowWithdrawal'].map((key) => <React.Fragment key={key}>
          <dt className="col-6 col-md-3 fw-normal text-muted">{t(`verificationAccess.${key}`)}</dt><dd className="col-6 col-md-3">{t(access[key] ? 'verificationAccess.allowed' : 'verificationAccess.blocked')}</dd>
        </React.Fragment>)}
        <dt className="col-6 col-md-3 fw-normal text-muted">{t('verificationAccess.firstDeposit')}</dt><dd className="col-6 col-md-3">{date(access.firstDepositAt)}</dd>
        <dt className="col-6 col-md-3 fw-normal text-muted">{t('verificationAccess.deadline')}</dt><dd className="col-6 col-md-3">{date(access.deadlineAt)}</dd>
        {['limit', 'used', 'reserved', 'remaining'].map((key) => <React.Fragment key={key}>
          <dt className="col-6 col-md-3 fw-normal text-muted">{t(`verificationAccess.${key}`)}</dt><dd className="col-6 col-md-3">{formatMoneyFromMinorUnits(access[key].amount, access[key].currency, locale)}</dd>
        </React.Fragment>)}
      </dl>
      {access.depositBlockReason && <p className="alert alert-warning py-2 mb-0" role="status">{t(reasonKey, { defaultValue: t('verificationAccess.depositBlocked') })}</p>}
    </>}
  </section>;
}
